import { useActiveAddress } from "@arweave-wallet-kit/react"
import { styled } from "@linaria/react"
import { Paper, Stack, Tooltip, Typography } from "@mui/material"
import { AlertTriangle } from "@untitled-ui/icons-react"
import type { ComponentProps } from "react"

import Skeleton from "./Skeleton"
import TokenLogo from "./TokenLogo"
import { NumberFont } from "../theme"
import { formatAddress, parseBigIntAsNumber } from "../utils/format"
import { formatNumber } from "../utils/number-utils"

export default function Token({
  balance,
  ticker,
  logo,
  id,
  name,
  active = false,
  disableHover = false,
  denomination,
  price,
  value,
  poolTvl,
  showSuggestions,
  disabled,
  approved,
  ...props
}: Props & ComponentProps<"div">) {
  const activeAddr = useActiveAddress()

  return (
    <Wrapper
      className="TokenWrapper"
      active={active}
      disableHover={disableHover}
      {...props}
      data-testid={`token-${id}`}
    >
      <TokenIconAndTicker>
        <TokenLogo tx={logo} alt={`${ticker} logo`} />
        <Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="subtitle2" fontWeight={700}>
              {name}
            </Typography>
            {!approved && (
              <Tooltip title="This token is not on the Community Approved list.">
                <div>
                  <AlertTriangle width={14} height={14} />
                </div>
              </Tooltip>
            )}
          </Stack>
          <Typography color="text.secondary" variant="caption" fontWeight={500}>
            {ticker} • {formatAddress(id, 4)}
            {typeof price === "number" && (
              <Typography fontFamily={NumberFont} variant="caption">
                {" "}
                • ${formatNumber(price, { maximumFractionDigits: 2 })}
              </Typography>
            )}
          </Typography>
        </Stack>
      </TokenIconAndTicker>
      <Stack direction="row" gap={2} alignItems="center">
        {/* {showSuggestions &&
          typeof poolTvl === "undefined" &&
          typeof balance === "bigint" &&
          balance !== 0n && (
            <Stack direction="row" gap={0.5} alignItems="center">
              <Paper
                variant="outlined"
                sx={{
                  backgroundColor: "var(--mui-palette-background-default)",
                  borderRadius: 0.25,
                  padding: 0.5,
                  boxShadow: "0px 2px 1.9px 0px rgba(0, 0, 0, 0.1)",

                  visibility: "hidden",
                  ".TokenWrapper:hover &": {
                    visibility: "visible",
                  },
                }}
                component={Stack}
              >
                <Typography color="accent.main" variant="caption" fontWeight={700} lineHeight={1}>
                  Be the first who provides liquidity for this pair.
                </Typography>
              </Paper>
              <PlusCircleSvg height={24} />
            </Stack>
          )} */}
        {disabled && (
          <Stack direction="row" gap={0.5} alignItems="center">
            <Paper
              variant="outlined"
              sx={{
                backgroundColor: "var(--mui-palette-background-default)",
                borderRadius: 0.25,
                padding: 0.5,
              }}
              component={Stack}
            >
              <Typography color="text.secondary" variant="caption" fontWeight={700} lineHeight={1}>
                Non-Transferable
              </Typography>
            </Paper>
          </Stack>
        )}
        <Stack alignItems="flex-end">
          {!activeAddr ? null : typeof balance === "bigint" ? (
            <Typography fontFamily={NumberFont} variant="subtitle2" fontWeight={700}>
              {formatNumber(parseBigIntAsNumber(balance, denomination), {
                maximumFractionDigits: 2,
              })}
            </Typography>
          ) : (
            <Skeleton width={"60px"} />
          )}
          {typeof value === "number" && value !== 0 && (
            <Typography color="text.secondary" fontFamily={NumberFont} variant="caption">
              $
              {formatNumber(value, {
                maximumFractionDigits: 2,
              })}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Wrapper>
  )
}

export const TokenSkeleton = () => (
  <Wrapper active={false} disableHover>
    <TokenIconAndTicker>
      <Skeleton width="1.7rem" height="1.7rem" borderRadius="100%" />
      <Skeleton width="5rem" height="1.1rem" />
    </TokenIconAndTicker>
    <Skeleton width="4rem" height="1rem" />
  </Wrapper>
)

const Wrapper = styled.div<{ active: boolean; disableHover: boolean }>`
  border: 1px solid var(--mui-palette-borderSecondary);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing) calc(var(--spacing) * 1.5);
  cursor: ${(props) => (!props.disableHover ? "pointer" : "normal")};
  transition: all 0.17s ease;
  border-radius: var(--mui-shape-borderRadius);
  background-color: ${(props) => (props.active ? "var(--mui-palette-action-focus)" : "")};

  &:hover {
    background-color: ${(props) => (props.disableHover ? "" : "var(--mui-palette-action-focus)")};
  }
`

export const TokenIconAndTicker = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: inherit;
  // text-transform: uppercase;
`

interface Props {
  balance?: bigint
  ticker?: string
  logo?: string
  active?: boolean
  disableHover?: boolean
  percentage?: string
  denomination?: number
  name?: string
  id: string
  price?: number
  value?: number
  poolTvl?: number
  showSuggestions?: boolean
  disabled?: boolean
  approved?: boolean
}
